import { z } from 'zod'

export const PremiumPlanDiscountSchema = z.object({
  originalPrice: z.string().trim().min(1),
  discountPercent: z.number(),
  finalPrice: z.string().trim().min(1),
})

export type PremiumPlanDiscount = z.infer<typeof PremiumPlanDiscountSchema>

const PremiumPlanDiscountEntrySchema = z.object({
  productId: z.string().trim().min(1),
  prices: z.unknown(),
})

const PricesRecordSchema = z.record(z.string(), z.unknown())

/** Product id → upper-cased currency code → configured prices. */
export type PremiumPlanDiscounts = Map<string, Map<string, PremiumPlanDiscount>>

const warn = (message: string, detail?: unknown) => {
  // eslint-disable-next-line no-console
  console.warn(`[premium-plan-discount] ${message}`, detail ?? '')
}

const parsePrices = (productId: string, prices: unknown) => {
  const byCurrency = new Map<string, PremiumPlanDiscount>()
  const record = PricesRecordSchema.safeParse(prices)
  if (!record.success) {
    warn(`prices of product ${productId} is not an object`)
    return byCurrency
  }

  const seenCurrencies = new Set<string>()
  const duplicatedCurrencies = new Set<string>()
  for (const [rawCurrency, value] of Object.entries(record.data)) {
    const currency = rawCurrency.trim().toUpperCase()
    if (seenCurrencies.has(currency)) {
      if (!duplicatedCurrencies.has(currency)) {
        warn(`currency ${currency} of product ${productId} is listed more than once`)
      }
      byCurrency.delete(currency)
      duplicatedCurrencies.add(currency)
      continue
    }
    seenCurrencies.add(currency)
    const price = PremiumPlanDiscountSchema.safeParse(value)
    if (!price.success) {
      warn(`currency ${currency} of product ${productId} is invalid`, price.error.issues)
      continue
    }
    byCurrency.set(currency, price.data)
  }
  return byCurrency
}

/**
 * Parses `NEXT_PUBLIC_PREMIUM_PLAN_DISCOUNT` without throwing. Only the faulty part is dropped:
 * unparseable JSON or a non-array disables everything, an entry without a product id is skipped,
 * a product id listed more than once is dropped, and an invalid currency only drops that currency.
 */
export const parsePremiumPlanDiscounts = (raw: string | undefined): PremiumPlanDiscounts => {
  const discounts: PremiumPlanDiscounts = new Map()
  if (!raw?.trim()) return discounts

  let payload: unknown
  try {
    payload = JSON.parse(raw)
  } catch (error) {
    warn('value is not valid JSON', error)
    return discounts
  }
  if (!Array.isArray(payload)) {
    warn('value is not an array')
    return discounts
  }

  const entries = payload.flatMap((item, index) => {
    const entry = PremiumPlanDiscountEntrySchema.safeParse(item)
    if (entry.success) return [entry.data]
    warn(`entry ${index} has no valid productId`)
    return []
  })

  const occurrences = new Map<string, number>()
  for (const { productId } of entries) {
    occurrences.set(productId, (occurrences.get(productId) ?? 0) + 1)
  }

  for (const { productId, prices } of entries) {
    if (occurrences.get(productId) !== 1) {
      if (!discounts.has(productId)) warn(`product ${productId} is listed more than once`)
      discounts.set(productId, new Map())
      continue
    }
    discounts.set(productId, parsePrices(productId, prices))
  }
  return discounts
}
